/*
Copyright (C) 2017  Cloudbase Solutions SRL
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import { observer } from "mobx-react";
import React from "react";
import styled from "styled-components";

import { TransferItemDetails } from "@src/@types/MainItem";
import { MinionPool } from "@src/@types/MinionPool";
import {
  ActionButton,
  ActionGroup,
  DetailsActions,
  DetailsTabs,
} from "@src/components/ui/DetailsKit";
import { CalendarClock, ClipboardCheck, Info, Play, Rocket, Stethoscope, Trash2 } from "lucide-react";
import UpcomingCutoverCard from "@src/components/modules/CutoverModule/UpcomingCutoverCard";
import EvidenceButton from "@src/components/modules/TransferModule/EvidenceButton";
import type { CutoverRequest } from "@src/@types/Cutover";
import Executions from "@src/components/modules/TransferModule/Executions";
import MainDetails from "@src/components/modules/TransferModule/MainDetails";
import Schedule from "@src/components/modules/TransferModule/Schedule";
import scheduleStore from "@src/stores/ScheduleStore";
import configLoader from "@src/utils/Config";

import type { Instance } from "@src/@types/Instance";
import type { Endpoint, StorageBackend } from "@src/@types/Endpoint";
import type { Execution, ExecutionTasks } from "@src/@types/Execution";
import type { Network } from "@src/@types/Network";
import type { Field } from "@src/@types/Field";
import type { Schedule as ScheduleType } from "@src/@types/Schedule";
const Wrapper = styled.div<any>`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;
const MainColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  min-width: 0;
`;
const DetailsBody = styled.div<any>`
  width: 100%;
  min-width: 0;
`;

const NavigationItems = [
  {
    label: "Overview",
    value: "",
    icon: Info,
  },
  {
    label: "Executions",
    value: "executions",
    icon: Play,
  },
  {
    label: "Schedule",
    value: "schedule",
    icon: CalendarClock,
  },
];

type TimezoneValue = "utc" | "local";
type Props = {
  item?: TransferItemDetails | null;
  itemId: string;
  endpoints: Endpoint[];
  sourceSchema: Field[];
  sourceSchemaLoading: boolean;
  destinationSchema: Field[];
  destinationSchemaLoading: boolean;
  networks: Network[];
  instancesDetails: Instance[];
  instancesDetailsLoading: boolean;
  scheduleStore: typeof scheduleStore;
  page: string;
  detailsLoading: boolean;
  executions: Execution[];
  executionsLoading: boolean;
  executionsPaginationLoading?: boolean;
  executionsTasksLoading: boolean;
  executionsTasks: ExecutionTasks[];
  minionPools: MinionPool[];
  storageBackends: StorageBackend[];
  hasOlderExecutions?: boolean;
  onLoadOlderExecutions?: () => void;
  onExecutionChange: (executionId: string) => void;
  onCancelExecutionClick: (
    execution: Execution | null,
    force?: boolean,
  ) => void;
  onDeleteExecutionClick: (execution: Execution | null) => void;
  onExecuteClick: () => void;
  onCreateDeploymentClick: () => void;
  onDeleteTransferClick: () => void;
  onAddScheduleClick: (schedule: ScheduleType) => void;
  onScheduleChange: (
    scheduleId: string | null,
    data: ScheduleType,
    forceSave?: boolean,
  ) => void;
  onScheduleRemove: (scheduleId: string | null) => void;
  onScheduleSave: (schedule: ScheduleType) => void;
  /** Swift: Deploy opens "Request cut-over" (config `cutoverApprovals`). */
  cutoverApprovals?: boolean;
  /** Swift: the transfer's active cut-over request, if any. */
  activeCutover?: CutoverRequest | null;
  onOpenCutover?: (id: string) => void;
  onCheckReadinessClick?: () => void;
};
type State = {
  timezone: TimezoneValue;
};
@observer
class TransferDetailsContent extends React.Component<Props, State> {
  state: State = {
    timezone: "local",
  };

  getLastExecution() {
    return this.props.executions?.length
      ? this.props.executions[this.props.executions.length - 1]
      : null;
  }

  getStatus() {
    return (
      this.props.item?.last_execution_status || this.getLastExecution()?.status
    );
  }

  isEndpointMissing() {
    return (
      this.props.endpoints.filter(
        e =>
          e.id === this.props.item?.origin_endpoint_id ||
          e.id === this.props.item?.destination_endpoint_id,
      ).length < 2
    );
  }

  handleTimezoneChange(timezone: TimezoneValue) {
    this.setState({ timezone });
  }

  renderBottomControls() {
    return (
      <DetailsActions>
        <ActionGroup>
          <ActionButton
            variant="secondary"
            disabled={this.getStatus() === "RUNNING"}
            onClick={this.props.onExecuteClick}
          >
            <Play size={16} />
            Execute now
          </ActionButton>
          {this.props.cutoverApprovals ? (
            <ActionButton
              disabled={this.isEndpointMissing() || Boolean(this.props.activeCutover)}
              title={
                this.props.activeCutover
                  ? "This transfer already has an active cut-over request"
                  : "Ask for approval to deploy this transfer inside a change window"
              }
              onClick={this.props.onCreateDeploymentClick}
              data-test-id="transfer-requestCutover"
            >
              <ClipboardCheck size={16} />
              Request cut-over
            </ActionButton>
          ) : (
            <ActionButton
              disabled={this.isEndpointMissing()}
              onClick={this.props.onCreateDeploymentClick}
            >
              <Rocket size={16} />
              Deploy
            </ActionButton>
          )}
          {this.props.onCheckReadinessClick ? (
            <ActionButton
              variant="secondary"
              disabled={this.isEndpointMissing()}
              onClick={this.props.onCheckReadinessClick}
              data-test-id="transfer-checkReadiness"
            >
              <Stethoscope size={16} />
              Check readiness
            </ActionButton>
          ) : null}
          {this.props.item ? <EvidenceButton kind="transfer" id={this.props.item.id} /> : null}
        </ActionGroup>
        <ActionButton variant="danger" onClick={this.props.onDeleteTransferClick}>
          <Trash2 size={16} />
          Delete transfer
        </ActionButton>
      </DetailsActions>
    );
  }

  renderMainDetails() {
    if (this.props.page !== "") {
      return null;
    }

    const cutover = this.props.activeCutover;
    return (
      <MainColumn>
        {cutover ? (
          <UpcomingCutoverCard
            request={cutover}
            transferInstances={this.props.item?.instances}
            onOpen={() => this.props.onOpenCutover?.(cutover.id)}
          />
        ) : null}
      <MainDetails
        item={this.props.item}
        storageBackends={this.props.storageBackends}
        minionPools={this.props.minionPools}
        sourceSchema={this.props.sourceSchema}
        sourceSchemaLoading={this.props.sourceSchemaLoading}
        destinationSchema={this.props.destinationSchema}
        destinationSchemaLoading={this.props.destinationSchemaLoading}
        instancesDetails={this.props.instancesDetails}
        instancesDetailsLoading={this.props.instancesDetailsLoading}
        loading={this.props.detailsLoading}
        endpoints={this.props.endpoints}
        networks={this.props.networks}
        bottomControls={this.renderBottomControls()}
      />
      </MainColumn>
    );
  }

  renderExecutions() {
    if (this.props.page !== "executions") {
      return null;
    }

    return (
      <Executions
        executions={this.props.executions}
        executionsTasks={this.props.executionsTasks}
        onCancelExecutionClick={this.props.onCancelExecutionClick}
        onDeleteExecutionClick={this.props.onDeleteExecutionClick}
        onExecuteClick={this.props.onExecuteClick}
        loading={this.props.executionsLoading || this.props.detailsLoading}
        paginationLoading={this.props.executionsPaginationLoading}
        onChange={this.props.onExecutionChange}
        tasksLoading={this.props.executionsTasksLoading}
        instancesDetails={this.props.instancesDetails}
        hasOlderExecutions={this.props.hasOlderExecutions}
        onLoadOlderExecutions={this.props.onLoadOlderExecutions}
      />
    );
  }

  renderSchedule() {
    if (this.props.page !== "schedule") {
      return null;
    }

    return (
      <Schedule
        disableExecutionOptions={configLoader.config.providersDisabledExecuteOptions.some(
          p =>
            p ===
            this.props.endpoints.find(
              e => e.id === this.props.item?.origin_endpoint_id,
            )?.type,
        )}
        schedules={this.props.scheduleStore.schedules}
        unsavedSchedules={this.props.scheduleStore.unsavedSchedules}
        adding={this.props.scheduleStore.adding}
        loading={this.props.scheduleStore.loading}
        onAddScheduleClick={this.props.onAddScheduleClick}
        onChange={this.props.onScheduleChange}
        onRemove={this.props.onScheduleRemove}
        onSaveSchedule={this.props.onScheduleSave}
        timezone={this.state.timezone}
        onTimezoneChange={timezone => {
          this.handleTimezoneChange(timezone);
        }}
        savingIds={this.props.scheduleStore.savingIds}
        enablingIds={this.props.scheduleStore.enablingIds}
        deletingIds={this.props.scheduleStore.deletingIds}
      />
    );
  }

  render() {
    return (
      <Wrapper>
        <DetailsTabs
          items={NavigationItems}
          selectedValue={this.props.page}
          href={item =>
            `/transfers/${this.props.itemId}${item.value ? `/${item.value}` : ""}`
          }
        />
        <DetailsBody>
          {this.renderMainDetails()}
          {this.renderExecutions()}
          {this.renderSchedule()}
        </DetailsBody>
      </Wrapper>
    );
  }
}

export default TransferDetailsContent;
